import React from 'react';
import { Button, CircularProgress, Tooltip } from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { toast } from 'react-toastify';
import api from '../api';

// Asks the AI to describe the figure the question needs, renders it as an SVG asset and hands
// back the stored URL. `visual` is the previously generated spec when there is one — passing it
// lets the model correct its own figure instead of starting blind.
export default function GenerateVisualButton({
  questionText,
  subject,
  subjectId = null,
  visual = null,
  options = null,
  onGenerated,
  disabled = false,
  size = 'small',
  label = 'Generate visual (AI)',
}) {
  const [busy, setBusy] = React.useState(false);
  const hasText = Boolean(String(questionText || '').replace(/<[^>]*>/g, '').trim());
  const optionTexts = Array.isArray(options)
    ? options.map((o) => String(o || '').trim()).filter(Boolean)
    : [];

  const handleClick = async () => {
    setBusy(true);
    try {
      const res = await api.post('admin/ai/regenerate-visual', {
        visual,
        question: questionText || '',
        subject: subject || '',
        subject_id: subjectId || null,
        strategy: 'respec',
        options: optionTexts.length >= 2 ? optionTexts : null,
        include_option_images: optionTexts.length >= 2,
      });
      const data = res?.data?.data;
      const url = data?.visual_image_url;
      const optionImages = data?.option_images || null;
      if (!url && !optionImages) {
        toast.error('Could not generate a visual for this question.');
        return;
      }
      onGenerated?.(url, data.visual, optionImages);
      if (optionImages) {
        toast.success(`Generated images for ${Object.keys(optionImages).length} answer option(s).`);
      }
      if (!url) {
        return;
      }
      if (data.verified === true) {
        toast.success('Visual generated and checked against the question. Review it before saving.');
      } else if (data.verified === false) {
        toast.warning('Visual generated, but it may not match the question — please double-check it.');
      } else {
        toast.success('Visual generated. Review it before saving.');
      }
    } catch (e) {
      toast.error(e?.response?.data?.message || 'Generating the visual failed.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Tooltip title={hasText ? 'Draw a diagram for this question with AI' : 'Add the question text first'}>
      <span>
        <Button
          size={size}
          variant="outlined"
          startIcon={busy ? <CircularProgress size={14} /> : <AutoAwesomeIcon />}
          onClick={handleClick}
          disabled={disabled || busy || !hasText}
          sx={{ textTransform: 'none' }}
        >
          {busy ? 'Generating…' : label}
        </Button>
      </span>
    </Tooltip>
  );
}
